import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Badge,
  Breadcrumbs,
  Button,
  CalendarDate,
  Card,
  CardHeader,
  ChevronDown,
  CoinsStacked01,
  Mail01,
  Menu,
  PageHeader,
  ProgressBar,
  RefreshCw01,
  SlashCircle01,
  Table,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { formatCents, scopeLabel } from "../../GiftCardProducts/giftCardFormat";
import GiftCardVoidRefundModal, { VOID_AND_REFUND } from "../../OrderPage/components/GiftCardVoidRefundModal";
import { canActOn } from "../../OrderPage/components/OrderGiftCardsCard";
import GiftCardAdjustModal from "./GiftCardAdjustModal";
import GiftCardReactivateModal from "./GiftCardReactivateModal";
import GiftCardRescheduleModal from "./GiftCardRescheduleModal";
import GiftCardResendModal from "./GiftCardResendModal";
import {
  CARD_STATUS_COLORS,
  CARD_STATUS_LABELS,
  CODE_ROTATION_REASON_LABELS,
  LEDGER_KIND_LABELS,
  PURCHASE_REFUND_KIND_LABELS,
  REFUND_STATE_COLORS,
  REFUND_STATE_LABELS,
  SOURCE_LABELS,
  formatDate,
  formatDateTime,
} from "../giftCardLabels";
import "../styles/giftCards.css";

const ADJUST = "adjust";
const RESEND = "resend";
const RESCHEDULE = "reschedule";
const REACTIVATE = "reactivate";
const VOID_REFUND = "void_refund";

const errorMessage = (error) => {
  const errors = error?.response?.data?.errors;
  return errors ? [].concat(errors).join(", ") : "Something went wrong. Please try again.";
};

const Fact = ({ label, children }) => (
  <div className="gift-card-detail-fact">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

Fact.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

Fact.defaultProps = {
  children: "—",
};

const deliveryLabel = (giftCard) => {
  if (giftCard.deliveredAt) return formatDateTime(giftCard.deliveredAt);
  if (giftCard.deliverAt) return `Scheduled for ${giftCard.deliverAtLabel || formatDateTime(giftCard.deliverAt)}`;
  return "Not yet";
};

const ledgerColumns = (currency) => [
  { key: "date", header: "Date", render: (entry) => formatDateTime(entry.createdAt) },
  { key: "kind", header: "Type", render: (entry) => LEDGER_KIND_LABELS[entry.kind] || entry.kind },
  {
    key: "amount",
    header: "Amount",
    align: "right",
    render: (entry) => (
      <span className={entry.amountCents > 0 ? "gift-card-detail-amount-credit" : undefined}>
        {`${entry.amountCents > 0 ? "+" : ""}${formatCents(entry.amountCents, currency)}`}
      </span>
    ),
  },
  {
    key: "balanceAfter",
    header: "Balance after",
    align: "right",
    render: (entry) => formatCents(entry.balanceAfterCents, currency),
  },
  { key: "venue", header: "Venue", render: (entry) => entry.venueName || "—" },
  {
    key: "order",
    header: "Event / order",
    render: (entry) =>
      entry.orderPath ? <a href={entry.orderPath}>{entry.eventName || `Order #${entry.orderId}`}</a> : "—",
  },
  { key: "who", header: "Who", render: (entry) => entry.actorName || "System" },
  { key: "note", header: "Note", render: (entry) => entry.note || "—" },
];

const GiftCardDetail = (initialProps) => {
  const [details, setDetails] = useState(initialProps);
  const [openModal, setOpenModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const {
    giftCard,
    ledger,
    purchaseRefunds,
    recipientChanges,
    deliveryReschedules = [],
    codeRotations = [],
    actions,
    voidRefund,
    listPath,
    detailPath,
  } = details;
  const money = (cents) => formatCents(cents, giftCard.currency);

  const reload = () => client.get(detailPath).then(({ data }) => setDetails(data));

  const perform = async (path, payload, successMessage) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await client.post(path, payload);
      toastSuccess(successMessage);
      setOpenModal(null);
      await reload();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const retryRefund = async (purchaseRefund) => {
    if (retryingId) return;
    setRetryingId(purchaseRefund.id);
    try {
      await client.post(purchaseRefund.retryPath);
      toastSuccess("Refund retry started");
      await reload();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setRetryingId(null);
    }
  };

  const resendOpen = actions.canResend && !actions.resendRefusal;
  const recipientChangeOpen = actions.canChangeRecipient && !actions.recipientChangeRefusal;

  const menuItems = [
    (actions.canAddValue || actions.canRemoveValue) && {
      key: ADJUST,
      label: "Adjust balance",
      icon: CoinsStacked01,
      disabled: Boolean(actions.adjustRefusal),
      description: actions.adjustRefusal,
      onClick: () => setOpenModal(ADJUST),
    },
    (actions.canResend || actions.canChangeRecipient) && {
      key: RESEND,
      label: actions.canChangeRecipient ? "Resend or change recipient" : "Resend email",
      icon: Mail01,
      disabled: !resendOpen && !recipientChangeOpen,
      description: resendOpen || recipientChangeOpen ? null : actions.resendRefusal,
      onClick: () => setOpenModal(RESEND),
    },
    actions.canReschedule &&
      giftCard.orderId &&
      !giftCard.deliveredAt && {
        key: RESCHEDULE,
        label: "Reschedule delivery",
        icon: CalendarDate,
        disabled: Boolean(actions.rescheduleRefusal),
        description: actions.rescheduleRefusal,
        onClick: () => setOpenModal(RESCHEDULE),
      },
    actions.canReactivate &&
      giftCard.status === "voided" && {
        key: REACTIVATE,
        label: "Reactivate",
        icon: RefreshCw01,
        disabled: Boolean(actions.reactivateRefusal),
        description: actions.reactivateRefusal,
        onClick: () => setOpenModal(REACTIVATE),
      },
    canActOn(voidRefund) && {
      key: VOID_REFUND,
      label: "Void or refund",
      icon: SlashCircle01,
      destructive: true,
      onClick: () => setOpenModal(VOID_REFUND),
    },
  ].filter(Boolean);

  const balancePercent =
    giftCard.initialValueCents > 0 ? Math.min(100, (giftCard.balanceCents / giftCard.initialValueCents) * 100) : 0;
  const source = SOURCE_LABELS[giftCard.source] || giftCard.source;

  return (
    <div className="gift-card-detail">
      <PageHeader
        breadcrumbs={<Breadcrumbs items={[{ label: "Gift cards", href: listPath }, { label: `•••• ${giftCard.last4}` }]} />}
        title={
          <span className="gift-card-detail-title">
            {`Gift card •••• ${giftCard.last4}`}
            <Badge color={CARD_STATUS_COLORS[giftCard.status]} dot size="sm">
              {CARD_STATUS_LABELS[giftCard.status]}
            </Badge>
          </span>
        }
        actions={
          menuItems.length > 0 && (
            <Menu
              placement="bottom-end"
              items={menuItems}
              trigger={
                <Button variant="secondary" size="sm" trailingIcon={ChevronDown}>
                  Actions
                </Button>
              }
            />
          )
        }
      />

      <div className="gift-card-detail-summary">
        <Card>
          <div className="gift-card-detail-balance-label">Balance</div>
          <div className="gift-card-detail-balance" data-testid="gift-card-balance">
            {money(giftCard.balanceCents)}
          </div>
          <div className="gift-card-detail-balance-of">{`of ${money(giftCard.initialValueCents)} starting value`}</div>
          <ProgressBar value={balancePercent} tone={giftCard.status === "active" ? "brand" : "muted"} />
        </Card>
        <Card>
          <dl className="gift-card-detail-facts">
            <Fact label="Status">{CARD_STATUS_LABELS[giftCard.status]}</Fact>
            <Fact label="Works at">{scopeLabel(giftCard.scope, giftCard.venueNames)}</Fact>
            {giftCard.expiredCents > 0 ? (
              <Fact label="Expired">{`${money(giftCard.expiredCents)} on ${formatDate(giftCard.expiredAt)}`}</Fact>
            ) : (
              <Fact label="Expires">{giftCard.expiresAt ? formatDate(giftCard.expiresAt) : "Never"}</Fact>
            )}
            <Fact label="Product">
              {giftCard.productPath ? <a href={giftCard.productPath}>{giftCard.productName}</a> : giftCard.productName}
            </Fact>
            <Fact label="Owner">{giftCard.ownerEmail}</Fact>
            <Fact label="Buyer">{giftCard.purchaserEmail}</Fact>
            <Fact label="Recipient">
              {giftCard.recipientEmail
                ? [giftCard.recipientName, giftCard.recipientEmail].filter(Boolean).join(" · ")
                : "For themselves"}
            </Fact>
            <Fact label="Source">
              {giftCard.orderPath ? <a href={giftCard.orderPath}>{`${source} · Order #${giftCard.orderId}`}</a> : source}
            </Fact>
            {giftCard.bonusForPath && (
              <Fact label="Bonus with">
                <a href={giftCard.bonusForPath}>{`Gift card •••• ${giftCard.bonusForLast4}`}</a>
              </Fact>
            )}
            <Fact label="Delivered">{deliveryLabel(giftCard)}</Fact>
            <Fact label="Issued">{formatDateTime(giftCard.createdAt)}</Fact>
            {giftCard.validFrom && <Fact label="Usable from">{formatDateTime(giftCard.validFrom)}</Fact>}
            {giftCard.giftUpGiftcardId && <Fact label="Gift Up card">{`#${giftCard.giftUpGiftcardId}`}</Fact>}
          </dl>
        </Card>
      </div>

      {purchaseRefunds.length > 0 && (
        <div className="gift-card-detail-section">
          <Card padding="none" header={<CardHeader title="Refunds to the buyer" supportingText="Money sent back to the card the buyer paid with." />}>
            <Table
              getRowKey={(purchaseRefund) => purchaseRefund.id}
              data={purchaseRefunds}
              columns={[
                { key: "date", header: "Date", render: (row) => formatDateTime(row.createdAt) },
                { key: "kind", header: "Type", render: (row) => PURCHASE_REFUND_KIND_LABELS[row.kind] || row.kind },
                { key: "amount", header: "Amount", align: "right", render: (row) => money(row.amountCents) },
                {
                  key: "state",
                  header: "Stripe",
                  render: (row) => (
                    <Badge color={REFUND_STATE_COLORS[row.state]} size="sm">
                      {REFUND_STATE_LABELS[row.state] || row.state}
                    </Badge>
                  ),
                },
                { key: "reason", header: "Reason", render: (row) => row.reason },
                {
                  key: "retry",
                  header: "",
                  align: "right",
                  render: (row) =>
                    row.retryPath ? (
                      <Button size="sm" variant="secondary" disabled={Boolean(retryingId)} onClick={() => retryRefund(row)}>
                        Retry refund
                      </Button>
                    ) : null,
                },
              ]}
            />
          </Card>
        </div>
      )}

      {recipientChanges.length > 0 && (
        <div className="gift-card-detail-section">
          <Card padding="none" header={<CardHeader title="Recipient changes" supportingText="Who moved this card, from whom, and why." />}>
            <Table
              getRowKey={(row) => row.id}
              data={recipientChanges}
              columns={[
                { key: "date", header: "Date", render: (row) => formatDateTime(row.createdAt) },
                { key: "who", header: "Who", render: (row) => row.actorName },
                { key: "from", header: "From", render: (row) => row.previousEmail || "—" },
                {
                  key: "to",
                  header: "To",
                  render: (row) =>
                    row.clearedScheduleLabel ? `${row.newEmail} · sent now instead of ${row.clearedScheduleLabel}` : row.newEmail,
                },
                { key: "note", header: "Note", render: (row) => row.note },
              ]}
            />
          </Card>
        </div>
      )}

      {deliveryReschedules.length > 0 && (
        <div className="gift-card-detail-section">
          <Card padding="none" header={<CardHeader title="Delivery changes" supportingText="Who moved this card's send time, and why." />}>
            <Table
              getRowKey={(row) => row.id}
              data={deliveryReschedules}
              columns={[
                { key: "date", header: "Date", render: (row) => formatDateTime(row.createdAt) },
                { key: "who", header: "Who", render: (row) => row.actorName },
                { key: "from", header: "From", render: (row) => row.previousDeliverAtLabel || "Right away" },
                { key: "to", header: "To", render: (row) => row.newDeliverAtLabel },
                { key: "note", header: "Note", render: (row) => row.note },
              ]}
            />
          </Card>
        </div>
      )}

      {codeRotations.length > 0 && (
        <div className="gift-card-detail-section">
          <Card padding="none" header={<CardHeader title="Code changes" supportingText="When the owner was given a new code." />}>
            <Table
              getRowKey={(row) => row.id}
              data={codeRotations}
              columns={[
                { key: "date", header: "Date", render: (row) => formatDateTime(row.createdAt) },
                { key: "who", header: "Who", render: (row) => row.actorName },
                { key: "reason", header: "How", render: (row) => CODE_ROTATION_REASON_LABELS[row.reason] || row.reason },
              ]}
            />
          </Card>
        </div>
      )}

      <div className="gift-card-detail-section">
        <Card padding="none" header={<CardHeader title="Ledger" supportingText="Every change to this card. Entries are never edited." />}>
          <Table columns={ledgerColumns(giftCard.currency)} data={ledger} getRowKey={(entry) => entry.id} />
        </Card>
      </div>

      {openModal === ADJUST && (
        <GiftCardAdjustModal
          giftCard={giftCard}
          canAdd={actions.canAddValue}
          canRemove={actions.canRemoveValue}
          maxAddCents={actions.maxAddCents}
          removableCents={actions.removableCents}
          submitting={submitting}
          onClose={() => setOpenModal(null)}
          onSubmit={({ direction, amountCents, note, idempotencyToken }) =>
            perform(
              actions.adjustPath,
              { direction, amount_cents: amountCents, note, idempotency_token: idempotencyToken },
              "Balance updated"
            )
          }
        />
      )}
      {openModal === RESEND && (
        <GiftCardResendModal
          giftCard={giftCard}
          canResend={resendOpen}
          canChangeRecipient={recipientChangeOpen}
          resendRefusal={actions.resendRefusal}
          submitting={submitting}
          onClose={() => setOpenModal(null)}
          onResend={() => perform(actions.deliveryPath, {}, `A new code is queued for ${giftCard.ownerEmail}`)}
          onChangeRecipient={({ recipientName, recipientEmail, note }) =>
            perform(
              actions.recipientChangePath,
              { recipient_name: recipientName, recipient_email: recipientEmail, note },
              `Recipient changed to ${recipientEmail}`
            )
          }
        />
      )}
      {openModal === RESCHEDULE && (
        <GiftCardRescheduleModal
          giftCard={giftCard}
          submitting={submitting}
          onClose={() => setOpenModal(null)}
          onSubmit={({ deliverAt, note }) =>
            perform(actions.deliveryReschedulePath, { deliver_at: deliverAt, note }, "Delivery rescheduled")
          }
        />
      )}
      {openModal === REACTIVATE && (
        <GiftCardReactivateModal
          giftCard={giftCard}
          submitting={submitting}
          onClose={() => setOpenModal(null)}
          onSubmit={({ reason }) => perform(actions.reactivatePath, { reason }, "Gift card reactivated")}
        />
      )}
      {openModal === VOID_REFUND && (
        <GiftCardVoidRefundModal
          giftCard={voidRefund}
          submitting={submitting}
          onClose={() => setOpenModal(null)}
          onSubmit={(choice, reason) =>
            choice === VOID_AND_REFUND
              ? perform(voidRefund.purchaseRefundUrl, { reason }, "Gift card voided and refund started")
              : perform(voidRefund.voidUrl, { reason }, "Gift card voided")
          }
        />
      )}
    </div>
  );
};

GiftCardDetail.propTypes = {
  giftCard: PropTypes.shape({
    id: PropTypes.number.isRequired,
    last4: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    currency: PropTypes.string,
    balanceCents: PropTypes.number.isRequired,
    initialValueCents: PropTypes.number.isRequired,
    scope: PropTypes.string.isRequired,
    venueNames: PropTypes.arrayOf(PropTypes.string),
    source: PropTypes.string.isRequired,
    productName: PropTypes.string,
    productPath: PropTypes.string,
    orderId: PropTypes.number,
    bonusForLast4: PropTypes.string,
    bonusForPath: PropTypes.string,
    orderPath: PropTypes.string,
    giftUpGiftcardId: PropTypes.number,
    ownerEmail: PropTypes.string,
    purchaserEmail: PropTypes.string,
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    validFrom: PropTypes.string,
    expiresAt: PropTypes.string,
    expiredCents: PropTypes.number.isRequired,
    expiredAt: PropTypes.string,
    deliverAt: PropTypes.string,
    deliverAtLabel: PropTypes.string,
    deliverAtLocal: PropTypes.string,
    deliveryTimeZone: PropTypes.string,
    rescheduleEarliest: PropTypes.string,
    rescheduleLatest: PropTypes.string,
    deliveredAt: PropTypes.string,
  }).isRequired,
  ledger: PropTypes.arrayOf(PropTypes.object).isRequired,
  purchaseRefunds: PropTypes.arrayOf(PropTypes.object).isRequired,
  recipientChanges: PropTypes.arrayOf(PropTypes.object).isRequired,
  deliveryReschedules: PropTypes.arrayOf(PropTypes.object),
  codeRotations: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.shape({
    canAddValue: PropTypes.bool,
    canRemoveValue: PropTypes.bool,
    maxAddCents: PropTypes.number.isRequired,
    removableCents: PropTypes.number.isRequired,
    adjustRefusal: PropTypes.string,
    adjustPath: PropTypes.string.isRequired,
    canReactivate: PropTypes.bool,
    reactivateRefusal: PropTypes.string,
    reactivatePath: PropTypes.string.isRequired,
    canResend: PropTypes.bool,
    canChangeRecipient: PropTypes.bool,
    resendRefusal: PropTypes.string,
    recipientChangeRefusal: PropTypes.string,
    deliveryPath: PropTypes.string.isRequired,
    recipientChangePath: PropTypes.string.isRequired,
    canReschedule: PropTypes.bool,
    rescheduleRefusal: PropTypes.string,
    deliveryReschedulePath: PropTypes.string,
  }).isRequired,
  voidRefund: PropTypes.object.isRequired,
  listPath: PropTypes.string.isRequired,
  detailPath: PropTypes.string.isRequired,
};

export default GiftCardDetail;
